import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { dataProvider } from '@/lib/dataProvider';

interface CourseModulesTabProps {
  courseId: string;
}

const CourseModulesTab: React.FC<CourseModulesTabProps> = ({ courseId }) => {
  const [isAddingModule, setIsAddingModule] = useState(false);
  const [selectedModuleIds, setSelectedModuleIds] = useState<string[]>([]);

  // Get modules from data provider
  const [courseModules, setCourseModules] = useState<any[]>(dataProvider.getCourseModules(courseId));

  // Get available modules from data provider
  const availableModules: any[] = dataProvider.getAvailableModules();

  const handleMoveModule = (moduleId: string, direction: 'up' | 'down') => {
    const moduleIndex = courseModules.findIndex(module => module.module_id === moduleId);
    if (
      (direction === 'up' && moduleIndex === 0) || 
      (direction === 'down' && moduleIndex === courseModules.length - 1)
    ) {
      return;
    }

    const newModules = [...courseModules];
    const targetIndex = direction === 'up' ? moduleIndex - 1 : moduleIndex + 1;
    
    // Swap modules
    [newModules[moduleIndex], newModules[targetIndex]] = [newModules[targetIndex], newModules[moduleIndex]];
    
    // Update order numbers
    const reorderedModules = newModules.map((module, index) => ({
      ...module,
      order: index + 1
    }));
    
    setCourseModules(reorderedModules);
  };

  const handleRemoveModule = (moduleId: string) => {
    const updatedModules = courseModules.filter(module => module.module_id !== moduleId);
    // Update order numbers
    const reorderedModules = updatedModules.map((module, index) => ({
      ...module,
      order: index + 1
    }));
    setCourseModules(reorderedModules);
  };

  const handleToggleModuleSelection = (moduleId: string) => {
    if (selectedModuleIds.includes(moduleId)) {
      setSelectedModuleIds(selectedModuleIds.filter(id => id !== moduleId));
    } else {
      setSelectedModuleIds([...selectedModuleIds, moduleId]);
    }
  };

  const handleAddSelectedModules = () => {
    // Get selected modules from available modules
    const modulesToAdd = availableModules
      .filter(module => selectedModuleIds.includes(module.module_id))
      .map((module, index) => ({
        module_id: module.module_id,
        module_code: module.module_code,
        name: module.name,
        description: module.description,
        version: module.current_version,
        order: courseModules.length + index + 1,
        delivery_methods: module.delivery_methods
      }));
    
    setCourseModules([...courseModules, ...modulesToAdd]);
    setSelectedModuleIds([]);
    setIsAddingModule(false);
  };

  const getDeliveryMethodIcon = (method: string) => {
    switch (method) {
      case 'e-learning':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
            <svg className="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
              <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
            </svg>
            E-learning
          </span>
        );
      case 'live-online':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
            <svg className="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm3 2h6v4H7V5zm8 8v2h1v-2h-1zm-2-2H7v4h6v-4zm2 0h1V9h-1v2zm1-4V5h-1v2h1zM5 5v2H4V5h1zm0 4H4v2h1V9zm-1 4h1v2H4v-2z" clipRule="evenodd" />
            </svg>
            Live-online
          </span>
        );
      case 'offline':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
            <svg className="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
            </svg>
            Offline
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Modules trong khóa học</h3>
        {!isAddingModule && (
          <button
            type="button"
            onClick={() => setIsAddingModule(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Thêm Module
          </button>
        )}
      </div>

      {/* List of modules in the course */}
      {courseModules.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-6 text-center border border-dashed border-gray-300">
          <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Chưa có module nào</h3>
          <p className="text-gray-500 mb-4">Thêm module để bắt đầu xây dựng khóa học</p>
          <button
            type="button"
            onClick={() => setIsAddingModule(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Thêm Module
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {courseModules.map((module, index) => (
            <div key={module.module_id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 flex justify-between items-center">
                <div className="flex items-center">
                  <span className="bg-gray-200 text-gray-700 rounded-full h-6 w-6 flex items-center justify-center text-sm font-medium mr-3">
                    {module.order}
                  </span>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{module.name}</h4>
                    <div className="text-xs text-gray-500 mt-1">
                      <span className="font-medium">{module.module_code}</span> • Phiên bản {module.version}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => handleMoveModule(module.module_id, 'up')}
                    disabled={index === 0}
                    className="text-gray-400 hover:text-gray-600 disabled:text-gray-300"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveModule(module.module_id, 'down')}
                    disabled={index === courseModules.length - 1}
                    className="text-gray-400 hover:text-gray-600 disabled:text-gray-300"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveModule(module.module_id)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-3">{module.description}</p>
                <div className="flex flex-wrap gap-2">
                  {module.delivery_methods.map((method: any) => (
                    <div key={method}>{getDeliveryMethodIcon(method)}</div>
                  ))}
                </div>
                <div className="mt-3 flex justify-end">
                  <Link
                    to={`/lms/modules/${module.module_id}`}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Xem chi tiết module
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Module selection modal */}
      {isAddingModule && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Thêm Module vào khóa học</h3>
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingModule(false);
                    setSelectedModuleIds([]);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Chọn các module bạn muốn thêm vào khóa học
              </p>
            </div>
            
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="space-y-4">
                {availableModules.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Không có module nào khả dụng để thêm</p>
                  </div>
                ) : (
                  availableModules.map((module) => (
                    <div 
                      key={module.module_id} 
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedModuleIds.includes(module.module_id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => handleToggleModuleSelection(module.module_id)}
                    >
                      <div className="flex items-start">
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-900">{module.name}</h4>
                          <div className="text-xs text-gray-500 mt-1">
                            <span className="font-medium">{module.module_code}</span> • Phiên bản {module.current_version}
                          </div>
                          <p className="text-sm text-gray-600 mt-2">{module.description}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {module.delivery_methods.map((method: any) => (
                              <div key={method}>{getDeliveryMethodIcon(method)}</div>
                            ))}
                          </div>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                          <div className={`w-5 h-5 rounded-full border ${
                            selectedModuleIds.includes(module.module_id)
                              ? 'bg-blue-600 border-blue-600'
                              : 'border-gray-300'
                          } flex items-center justify-center`}>
                            {selectedModuleIds.includes(module.module_id) && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setIsAddingModule(false);
                  setSelectedModuleIds([]);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleAddSelectedModules}
                disabled={selectedModuleIds.length === 0}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
              >
                Thêm {selectedModuleIds.length} module đã chọn
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseModulesTab; 